import React, { useState, useEffect } from "react";
import { Skeleton, Box, Stack, Typography } from "@mui/material";
import { Map, AdvancedMarker, MapMouseEvent, useMap } from "@vis.gl/react-google-maps";

import { LocationPickerContainer } from "./styles";
import LocationSearch from "./LocationSearch";
import { InvitationCoordinates } from "../../../store/services/invitations/dto/invitations.dto";
import { DEFAULT_COORDINATES } from "../../../constants";

const LocationPicker = ({
	fieldValueState,
	setFieldValueState
}: {
	fieldValueState: InvitationCoordinates;
	setFieldValueState: React.Dispatch<React.SetStateAction<InvitationCoordinates>>;
}) => {
	const map = useMap();
	const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(undefined);

	useEffect(() => {
		if (map) {
			map.panTo(fieldValueState || DEFAULT_COORDINATES);
			map.setZoom(15);
		}
	}, [map]);

	useEffect(() => {
		if (!navigator.geolocation) {
			console.log("Geolocation not supported");
			setCenter(DEFAULT_COORDINATES);
			return;
		}

		const getLocation = () => {
			navigator.geolocation.getCurrentPosition(
				position => {
					const coords = {
						lat: position.coords.latitude,
						lng: position.coords.longitude
					};
					setCenter(coords);
				},
				error => {
					console.log("Location error:", error);
					// fallback if denied
					setCenter(DEFAULT_COORDINATES);
				},
				{
					enableHighAccuracy: true,
					timeout: 5000
				}
			);
		};

		navigator.permissions?.query({ name: "geolocation" as PermissionName }).then(result => {
			if (result.state === "granted") {
				getLocation();
			}

			if (result.state === "prompt") {
				// Trigger permission request
				getLocation();
			}

			if (result.state === "denied") {
				console.log("User denied location");
			}

			result.onchange = () => {
				if (result.state === "granted") {
					getLocation();
				}
			};
		});
	}, []);

	const handleMapClick = (event: MapMouseEvent) => {
		if (event.detail) {
			setFieldValueState(event.detail.latLng as InvitationCoordinates);
		}
	};

	if (!center)
		return (
			<Box display={"flex"} flexDirection={"column"} gap={1} width={"100%"} p={2} boxSizing={"border-box"}>
				<Stack spacing={1}>
					<Skeleton variant={"rectangular"} height={"50px"} />
					<Skeleton variant={"rectangular"} height={"400px"} />
				</Stack>
			</Box>
		);

	return (
		<LocationPickerContainer>
			<Map
				mapId={"1"}
				defaultZoom={14}
				defaultCenter={center}
				style={{ width: "100%", height: "400px", borderRadius: "5px", boxShadow: "0 0 1px 2px #b9b9b9" }}
				onClick={handleMapClick}
			>
				<div className="autocomplete-container">
					<LocationSearch onSelect={setFieldValueState} />
				</div>
				{<AdvancedMarker position={fieldValueState ? fieldValueState : DEFAULT_COORDINATES} />}
			</Map>

			<Box mt={2} width={"100%"} display={"flex"} flexDirection={"column"} justifyContent={"center"}>
				<Typography textAlign={"center"} fontWeight={"bold"}>
					Coordinates
				</Typography>

				<Typography textAlign={"center"}>
					{(fieldValueState || DEFAULT_COORDINATES).lat}, {(fieldValueState || DEFAULT_COORDINATES).lng}
				</Typography>
			</Box>
		</LocationPickerContainer>
	);
};

export default LocationPicker;
